import React, { useState } from 'react';
import { Style } from '../../types/inventory';
import { Plus, Edit, Eye, Package, ChevronLeft, ChevronRight, Image, Loader2 } from 'lucide-react';

interface StyleManagementProps {
  styles: Style[];
  loading: boolean;
  pagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    per_page: number;
  };
  onPageChange: (page: number) => void;
}

const StyleManagement: React.FC<StyleManagementProps> = ({
  styles,
  loading,
  pagination,
  onPageChange
}) => {  const [selectedStyle, setSelectedStyle] = useState<Style | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  const handleCreateStyle = () => {
    // TODO: Implement create modal
    console.log('Create style button clicked');
  };

  const handleViewStyle = (style: Style) => {
    setSelectedStyle(style);
    setShowDetailsModal(true);
  };

  const handleEditStyle = (style: Style) => {
    setSelectedStyle(style);
    // Would open edit modal
    console.log('Edit style:', style);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Style Management</h2>
          <p className="text-gray-600">Manage product styles and their variations</p>
        </div>
        <button
          onClick={handleCreateStyle}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Style
        </button>
      </div>

      {/* Styles Grid */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Loading styles...</p>
        </div>
      ) : styles.length === 0 ? (
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Styles Found</h3>
          <p className="text-gray-600">Create your first style to get started</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {styles.map((style) => (
              <div key={style.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                {/* Style Image */}
                <div className="aspect-square bg-gray-100 relative">
                  {style.images && style.images.length > 0 ? (
                    <img
                      src={style.images[0]}
                      alt={style.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Jean Badge */}
                  {style.is_jeans && (
                    <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                      Jeans
                    </div>
                  )}
                </div>

                {/* Style Info */}
                <div className="p-4">
                  <div className="mb-3">
                    <h3 className="font-medium text-gray-900 truncate">{style.name}</h3>
                    <p className="text-sm text-gray-600">{style.style_code}</p>
                  </div>

                  {/* Style Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sizes:</span>
                      <span className="text-gray-900">{style.sizes.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Colors:</span>
                      <span className="text-gray-900">{style.colors.length}</span>
                    </div>
                  </div>

                  {/* Colors Preview */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-600 mb-2">Available Colors:</p>
                    <div className="flex flex-wrap gap-1">
                      {style.colors.slice(0, 6).map((color, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded"
                        >
                          {color}
                        </span>
                      ))}
                      {style.colors.length > 6 && (
                        <span className="inline-block px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded">
                          +{style.colors.length - 6}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewStyle(style)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </button>
                    <button
                      onClick={() => handleEditStyle(style)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => onPageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => onPageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.current_page - 1) * pagination.per_page + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total_records)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total_records}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                    <button
                      onClick={() => onPageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === pagination.current_page
                              ? 'z-10 bg-blue-600 text-white focus:z-20'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => onPageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page === pagination.total_pages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Style Details Modal */}
      {showDetailsModal && selectedStyle && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Style Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Style Code</label>
                  <p className="text-sm text-gray-900">{selectedStyle.style_code}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-sm text-gray-900">{selectedStyle.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <p className="text-sm text-gray-900">{selectedStyle.is_jeans ? 'Jeans' : 'Regular'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Available Sizes</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedStyle.sizes.map((size, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {size}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Available Colors</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedStyle.colors.map((color, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                        {color}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedStyle(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StyleManagement;
